/* iodine-form-validation.js */
/** file contains the `initializeIodine()` function to inilize Iodine */

/* import idodine for frontend validation */
import '@kingshott/iodine';
import IBAN from 'iban';
import { config as germanTranslationErros } from './german-iodine-messages';

/* global Iodine */
export default function initializeIodine() {
    const defaultFieldName = 'Die Eingabe';
    document.addEventListener('alpine:init', () => {
        /* eslint-disable no-undef */
        Alpine.data('form', () => ({
            showPassword: false,
            showTextInput: false,
            inputElements: [],
            ele: [],
            parsedRules: {}, // Cache for parsed rules to avoid repeated JSON.parse()
            init: function () {
                // add another rule (for names)
                Iodine.addRule('name', (value) =>
                    /^[a-zA-ZàèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝâêîôûÂÊÎÔÛãñõÃÑÕäëïöüÿÄËÏÖÜŸçÇßØøÅåÆæœ]+(([',. --()]?[a-zA-ZàèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝâêîôûÂÊÎÔÛãñõÃÑÕäëïöüÿÄËÏÖÜŸçÇßØøÅåÆæœ])?[a-zA-ZàèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝâêîôûÂÊÎÔÛãñõÃÑÕäëïöüÿÄËÏÖÜŸçÇßØøÅåÆæœ]*)*$/.test(
                        value.trim()
                    )
                );

                Iodine.addRule('containsNumber', (value) => /^(?=.*\d).+$/.test(value));

                Iodine.addRule('street', (value) =>
                    /^((\d+\.?)+(\s?))*(([a-zäöüàâçéèêëîïôûùüÿñæœß\-./()']{1,})+(\s?))+(([a-zäöüàâçéèêëîïôûùüÿñæœß\-./()']{2,})+(\s?))+(\d+\.?)*(\s?)+(-?)(\/?)(\d*)+([a-z]?)+(\s?)+([a-z]?)+(\d*)+([a-z]?)$/i.test(
                        value.trim()
                    )
                );

                Iodine.addRule('postal_code', (value) => value.length === 4 || value.length === 5);

                Iodine.addRule(
                    'radio',
                    (value, name) => document.querySelector(`input[name="${name}"]:checked`) != null
                );

                Iodine.addRule(
                    'checkbox',
                    (value, name) => document.querySelector(`input[name="${name}"]:checked`) != null
                );

                Iodine.addRule(
                    'select',
                    (value, name) =>
                        document.querySelector(`select[name="${name}"]`).options[
                            document.querySelector(`select[name="${name}"]`).selectedIndex
                        ].value
                );

                Iodine.addRule('iban', (value) => IBAN.isValid(value.trim()));

                Iodine.addRule('bic', (value) =>
                    value
                        ? /^([a-zA-Z]{4})([a-zA-Z]{2})(([2-9a-zA-Z]{1})([0-9a-np-zA-NP-Z]{1}))((([0-9a-wy-zA-WY-Z]{1})([0-9a-zA-Z]{2}))|([xX]{3})|)?$/.test(
                              value.trim().toUpperCase()
                          )
                        : false
                );

                Iodine.addRule('phone', (value) => /^(\+)?([ 0-9]){7,16}$/.test(value.trim()));

                // Set up german translation
                Iodine.setErrorMessages(germanTranslationErros);

                // Store an array of all the input elements with 'data-rules' attributes
                this.inputElements = [
                    ...this.$el.querySelectorAll('input[data-rules]'),
                    ...this.$el.querySelectorAll('select[data-rules]'),
                    ...this.$el.querySelectorAll('textarea[data-rules]'),
                ];
                this.initDomData();
                // Don't validate on init - fields haven't been interacted with yet
                // Only show server errors that came from backend
                this.showServerErrors();
            },
            initDomData: function () {
                // Create an object attached to the component state for each input element to store its state
                // Also pre-parse and cache rules for better performance
                this.inputElements.map((ele) => {
                    const errors = ele.dataset.serverErrors.replaceAll('`', '"');
                    const rules = ele.dataset.rules.replaceAll('`', '"');
                    this.ele[`${ele.name}`] = {
                        serverErrors: JSON.parse(errors),
                        blurred: false,
                        errorMessage: '',
                    };
                    // Cache parsed rules to avoid repeated JSON.parse on every keystroke
                    this.parsedRules[ele.name] = JSON.parse(rules);
                    return true;
                });

                /* TODO: refactor this part to be reusable */
                let errorElement = document.querySelectorAll('form .err');
                errorElement = errorElement.length ? errorElement[0] : null;
                if (errorElement) {
                    dispatchEvent(new CustomEvent('theres-an-error-on-site'));
                }
            },
            showServerErrors: function () {
                // Only show server errors on init, don't run validation
                this.inputElements.map((ele) => {
                    if (this.ele[`${ele.name}`].serverErrors.length > 0) {
                        this.ele[`${ele.name}`].errorMessage = this.ele[`${ele.name}`].serverErrors.join(' | ');
                    }
                    return true;
                });
            },
            validateField: function (fieldName) {
                // Validate a single field and update its error message
                const ele = this.inputElements.find((input) => input.name === fieldName);
                if (ele && this.ele[fieldName]) {
                    this.ele[fieldName].errorMessage = this.getErrorMessage(ele);
                    return this.ele[fieldName].errorMessage;
                }
                return '';
            },
            validateAllFields: function () {
                // Validate all fields (used on form submit)
                this.inputElements.map((ele) => {
                    this.ele[`${ele.name}`].errorMessage = this.getErrorMessage(ele);
                    return this.ele[`${ele.name}`].errorMessage;
                });
            },
            updateErrorMessages: function (fieldName = null) {
                // Keep for backwards compatibility
                if (fieldName) {
                    return this.validateField(fieldName);
                }
                this.validateAllFields();
            },
            getErrorMessage: function (ele) {
                // Return any server errors if they're present
                if (this.ele[`${ele.name}`].serverErrors.length > 0) {
                    return this.ele[`${ele.name}`].serverErrors.join(' | ');
                }
                // Only validate if the field has been blurred
                if (!this.ele[`${ele.name}`].blurred) {
                    return '';
                }
                // Use cached parsed rules instead of parsing on every call
                const rules = this.parsedRules[ele.name];
                const error = Iodine.is(ele.value, rules);
                const fieldNameAttr = ele.dataset.attribute ? ele.dataset.attribute : defaultFieldName;
                if (error !== true) {
                    return Iodine.getErrorMessage(error, { field: fieldNameAttr });
                }
                // return empty string if there are no errors
                return '';
            },
            submit: function (event) {
                const invalidElements = this.inputElements.filter((input) => {
                    // Use cached parsed rules
                    const rules = this.parsedRules[input.name];
                    return Iodine.is(input.value, rules) !== true;
                });
                if (invalidElements.length > 0) {
                    event.preventDefault();
                    let errorElement = document.querySelectorAll('form .err');
                    errorElement = errorElement.length ? errorElement[0] : null;
                    if (errorElement) {
                        dispatchEvent(new CustomEvent('theres-an-error-on-site'));
                    }

                    // We set all the inputs as blurred if the form has been submitted
                    this.inputElements.map((input) => {
                        this.ele[`${input.name}`].blurred = true;
                        return true;
                    });
                    // And validate all fields to show errors.
                    this.validateAllFields();
                }
            },
            change: function (event) {
                // Ignore all events that aren't coming from the inputs we're watching
                if (!this.ele[`${event.target.name}`]) {
                    return false;
                }
                if (event.type === 'input') {
                    // On input events, only clear server errors - don't validate
                    // This is critical for mobile performance
                    this.ele[`${event.target.name}`].serverErrors = [];
                    // Clear error message immediately for responsive feel, but don't validate
                    if (!this.ele[`${event.target.name}`].blurred) {
                        this.ele[`${event.target.name}`].errorMessage = '';
                    }
                }
                if (event.type === 'focusout') {
                    // On focusout, mark as blurred and validate the field
                    this.ele[`${event.target.name}`].blurred = true;
                    this.validateField(event.target.name);
                }
            },
        }));
    });
}
